import { zodResolver } from '@hookform/resolvers/zod';
import {
  formatCount,
  formatRatePercent,
  normalizeSearchText,
  toPersianDigits,
} from '@saay/formatting';
import { Button as ShadButton } from '@saay/shadcn/components/button';
import { Popover, PopoverContent, PopoverTrigger } from '@saay/shadcn/components/popover';
import {
  Table as ShadTable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@saay/shadcn/components/table';
import { PencilIcon, XIcon } from 'lucide-react';
import { type ReactNode, type RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router';
import { z } from 'zod';

import type { GeographyArea, PhysicalCardCityUsage, PhysicalCardNumberingUsage } from '../api.js';
import { FilterPill, FilterPillBar } from '../components/filter-pills.js';
import { QueryState } from '../components/guard.js';
import { ListSearchInput } from '../components/list-filters.js';
import { SurfaceSection } from '../components/surface-section.js';
import {
  Button,
  CapacityMeter,
  CommandFeedback,
  EmptyState,
  FieldErrorList,
  IconButton,
  Menu,
  MenuItem,
  NativeSelect,
  SegmentedButtons,
  Skeleton,
  StatusChip,
  TextField,
  useWindowClass,
} from '../kit.js';
import { useAllNumberingCities, useCities, useNumberingUsage, useProvinces } from '../queries.js';
import {
  both,
  capBelowLiveWarning,
  capProblem,
  CITY_PARAM,
  type CityState,
  cityCodeProblem,
  cityTagOf,
  type CodeWidth,
  defaultReason,
  digitsOnly,
  dropCity,
  EDIT_PARAM,
  editTargetOf,
  nextCityCode,
  nextProvinceDigits,
  type NumberingChange,
  putCity,
  putProvince,
  REASON_PRESETS,
  setCityCap,
  usedCityCodes,
  valuesOf,
} from './physical-card-config.js';
import { CAP_STATE, capStateEntry, cityTagLabel, WIDTH_LABEL } from './physical-card-numbering.js';
import {
  ChangeDialog,
  ProblemLine,
  ReasonPicker,
  reasonProblem,
  useNumberingSave,
} from './physical-card-save.js';

/**
 * «شهرها» (D-117): the one place for cities, usage and configuration in one table. Configured
 * cities come from K9 (AC §31.11.4); the cities without a code that have waiting buyers come from K8
 * (§31.11.3) and read «بدون کد». The Super Admin edits a city's cap inline (a pencil opens a small
 * popover), adds or edits a city in a small dialog, deletes a city without cards, and defines the
 * code of a waiting city in the add dialog, pre-filled. Every action posts the whole configuration
 * (AC §31.6). The rows are filtered here (the list holds at most 1,000 cities plus 100 waiting) and
 * shown 25 at a time; on a phone each row is a card.
 */

const PAGE_SIZE = 25;

interface ConfiguredRow {
  readonly cityId: string;
  readonly cityNameFa: string;
  readonly kind: 'configured';
  readonly provinceId: string;
  readonly provinceNameFa: string;
  readonly usage: PhysicalCardCityUsage;
}

interface UnconfiguredRow {
  readonly cityId: string;
  readonly cityNameFa: string;
  readonly kind: 'unconfigured';
  /** Resolved from the province's name (K8 names it only); `undefined` when no province matches. */
  readonly provinceId: string | undefined;
  readonly provinceNameFa: string;
  readonly waitingBuyerCount: number;
}

export type CityRow = ConfiguredRow | UnconfiguredRow;

const NONE_LABEL = 'بدون کد';

const STATE_OPTIONS: readonly { readonly label: string; readonly value: CityState }[] = [
  { label: CAP_STATE.OK.label, value: 'OK' },
  { label: CAP_STATE.NEAR_CAP.label, value: 'NEAR_CAP' },
  { label: CAP_STATE.AT_CAP.label, value: 'AT_CAP' },
  { label: NONE_LABEL, value: 'NONE' },
];

function stateOf(row: CityRow): string {
  return row.kind === 'configured' ? row.usage.capState : 'NONE';
}

/** The waiting cities first (they have no code to sort by), then the configured ones by tag. */
export function cityRows(
  configured: readonly PhysicalCardCityUsage[],
  usage: PhysicalCardNumberingUsage | undefined,
  provinces: readonly GeographyArea[],
): CityRow[] {
  const provinceByName = new Map(provinces.map((item) => [item.nameFa, item.id]));
  const waiting: CityRow[] = (usage?.waiting.unconfiguredCities ?? []).map((city) => ({
    cityId: city.cityId,
    cityNameFa: city.cityNameFa,
    kind: 'unconfigured',
    provinceId: provinceByName.get(city.provinceNameFa),
    provinceNameFa: city.provinceNameFa,
    waitingBuyerCount: city.waitingBuyerCount,
  }));
  return [
    ...waiting,
    ...configured.map((row): CityRow => ({
      cityId: row.cityId,
      cityNameFa: row.cityNameFa,
      kind: 'configured',
      provinceId: row.provinceId,
      provinceNameFa: row.provinceNameFa,
      usage: row,
    })),
  ];
}

type Dialog =
  | {
      readonly kind: 'add';
      readonly cityId?: string | undefined;
      readonly provinceId?: string | undefined;
    }
  | { readonly kind: 'edit'; readonly row: ConfiguredRow }
  | { readonly kind: 'drop'; readonly row: ConfiguredRow };

type FilterKey = 'province' | 'q' | 'state' | typeof CITY_PARAM;

export function CitiesPanel({ manage }: { readonly manage: boolean }) {
  const [params, setParams] = useSearchParams();
  const provinceId = params.get('province') ?? '';
  const state = params.get('state') ?? '';
  const query = params.get('q') ?? '';
  const onlyCity = params.get(CITY_PARAM) ?? '';
  const usage = useNumberingUsage();
  const cities = useAllNumberingCities();
  const geography = useProvinces();
  const windowClass = useWindowClass('expanded');
  const [capOpen, setCapOpen] = useState<string | undefined>();
  const [dialog, setDialog] = useState<Dialog | undefined>();
  // The one-time action of a «نیاز به اقدام» link, done once the rows are known.
  const [pending] = useState(() => (manage ? editTargetOf(params.get(EDIT_PARAM)) : undefined));
  const done = useRef(false);

  useEffect(() => {
    if (!params.has(EDIT_PARAM)) return;
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete(EDIT_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [params, setParams]);

  const rows = useMemo(
    () => cityRows(cities.data ?? [], usage.data, geography.data ?? []),
    [cities.data, usage.data, geography.data],
  );
  const ready =
    cities.data !== undefined && usage.data !== undefined && geography.status !== 'pending';

  useEffect(() => {
    if (done.current || pending === undefined || !ready || onlyCity === '') return;
    done.current = true;
    const row = rows.find((item) => item.cityId === onlyCity);
    if (pending === 'cap' && row?.kind === 'configured') setCapOpen(row.cityId);
    if (pending === 'code' && row?.kind === 'unconfigured') {
      setDialog({ cityId: row.cityId, kind: 'add', provinceId: row.provinceId });
    }
  }, [onlyCity, pending, ready, rows]);

  const provinceOf = useMemo(() => {
    const map = new Map((cities.data ?? []).map((row) => [row.cityId, row.provinceId]));
    return (cityId: string) => map.get(cityId);
  }, [cities.data]);

  const needle = normalizeSearchText(query.trim());
  const digitNeedle = digitsOnly(query);
  const filtered = rows.filter(
    (row) =>
      (onlyCity === '' || row.cityId === onlyCity) &&
      (provinceId === '' || row.provinceId === provinceId) &&
      (state === '' || stateOf(row) === state) &&
      (needle === '' ||
        normalizeSearchText(row.cityNameFa).includes(needle) ||
        (digitNeedle !== '' &&
          row.kind === 'configured' &&
          row.usage.cityTag.startsWith(digitNeedle))),
  );
  const filterKey = `${onlyCity}|${provinceId}|${state}|${needle}`;
  const [paging, setPaging] = useState({ key: filterKey, limit: PAGE_SIZE });
  const limit = paging.key === filterKey ? paging.limit : PAGE_SIZE;
  const shown = filtered.slice(0, limit);
  const filteredOn = onlyCity !== '' || provinceId !== '' || state !== '' || needle !== '';

  const provinceOptions = useMemo(() => {
    const names = new Map<string, string>();
    for (const row of rows)
      if (row.provinceId !== undefined) names.set(row.provinceId, row.provinceNameFa);
    return [...names]
      .map(([value, label]) => ({ label, value }))
      .sort((left, right) => left.label.localeCompare(right.label, 'fa'));
  }, [rows]);

  function set(changes: Partial<Readonly<Record<FilterKey, string>>>) {
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        for (const [key, value] of Object.entries(changes)) {
          if (value === '') next.delete(key);
          else next.set(key, value);
        }
        return next;
      },
      { replace: true },
    );
  }

  const clearAll = () => set({ [CITY_PARAM]: '', province: '', q: '', state: '' });
  const onlyCityName = rows.find((row) => row.cityId === onlyCity)?.cityNameFa;
  const failed = cities.status === 'error' ? cities : usage.status === 'error' ? usage : null;
  const actions: RowActions = {
    capOpen,
    manage,
    onAddCode: (row) => setDialog({ cityId: row.cityId, kind: 'add', provinceId: row.provinceId }),
    onCapOpen: (cityId, open) => setCapOpen(open ? cityId : undefined),
    onDrop: (row) => setDialog({ kind: 'drop', row }),
    onEdit: (row) => setDialog({ kind: 'edit', row }),
  };

  return (
    <SurfaceSection
      {...(manage
        ? {
            action: (
              <Button icon="plus" onClick={() => setDialog({ kind: 'add' })} size="sm">
                افزودن شهر
              </Button>
            ),
          }
        : {})}
      id="physical-cards-cities"
      title="شهرها"
    >
      <ListSearchInput
        label="جست‌وجوی شهر"
        onChange={(event) => set({ q: event.target.value })}
        placeholder="نام یا کد شهر"
        value={query}
      />
      <FilterPillBar
        activeFilterCount={
          (provinceId === '' ? 0 : 1) + (state === '' ? 0 : 1) + (onlyCity === '' ? 0 : 1)
        }
        label="فیلتر شهرها"
        onClearAll={clearAll}
      >
        {onlyCity === '' ? null : (
          <span className="inline-flex h-9 shrink-0 items-center gap-1 rounded-full bg-primary/10 ps-3 pe-1 text-sm text-primary">
            {`فقط ${onlyCityName ?? 'یک شهر'}`}
            <ShadButton
              aria-label="نمایش همهٔ شهرها"
              className="size-7 rounded-full text-primary hover:bg-primary/15"
              onClick={() => set({ [CITY_PARAM]: '' })}
              size="icon-sm"
              type="button"
              variant="ghost"
            >
              <XIcon aria-hidden="true" className="size-3.5" />
            </ShadButton>
          </span>
        )}
        <FilterPill
          emptyLabel="همهٔ استان‌ها"
          label="استان"
          onChange={(value) => set({ province: value })}
          options={provinceOptions}
          value={provinceId}
        />
        <FilterPill
          emptyLabel="همه"
          label="وضعیت"
          onChange={(value) => set({ state: value })}
          options={STATE_OPTIONS}
          value={state}
        />
      </FilterPillBar>
      <QueryState
        empty={
          filteredOn ? (
            <EmptyState
              action={
                <Button onClick={clearAll} size="sm" variant="secondary">
                  پاک‌کردن فیلترها
                </Button>
              }
              icon="search"
              title="با این فیلترها شهری پیدا نشد"
            />
          ) : (
            <EmptyState
              {...(manage
                ? {
                    action: (
                      <Button icon="plus" onClick={() => setDialog({ kind: 'add' })} size="sm">
                        افزودن شهر
                      </Button>
                    ),
                  }
                : {})}
              title="هنوز برای هیچ شهری کد تعریف نشده"
            />
          )
        }
        error={failed?.error}
        isEmpty={filtered.length === 0}
        onRetry={() => {
          void cities.refetch();
          void usage.refetch();
        }}
        skeleton={<Skeleton label="در حال بارگذاری شهرها…" lines={6} />}
        status={
          failed !== null
            ? 'error'
            : cities.status === 'success' && usage.status === 'success'
              ? 'success'
              : 'pending'
        }
      >
        <p aria-live="polite" className="text-xs text-muted-foreground">
          {shown.length < filtered.length
            ? `${formatCount(shown.length)} از ${formatCount(filtered.length)} شهر`
            : `${formatCount(filtered.length)} شهر`}
        </p>
        {windowClass === 'compact' ? (
          <CityCards actions={actions} rows={shown} />
        ) : (
          <CityTable actions={actions} rows={shown} />
        )}
        {shown.length < filtered.length ? (
          <Button
            className="self-center"
            onClick={() => setPaging({ key: filterKey, limit: limit + PAGE_SIZE })}
            size="sm"
            variant="secondary"
          >
            {`نمایش ${formatCount(Math.min(PAGE_SIZE, filtered.length - shown.length))} شهر دیگر`}
          </Button>
        ) : null}
      </QueryState>
      {manage && dialog !== undefined ? (
        dialog.kind === 'drop' ? (
          <DropCityDialog onClose={() => setDialog(undefined)} row={dialog.row} />
        ) : (
          <CityDialog
            dialog={dialog}
            onClose={() => setDialog(undefined)}
            provinceOf={provinceOf}
            provinces={geography.data ?? []}
            provincesFailed={geography.status === 'error'}
          />
        )
      ) : null}
    </SurfaceSection>
  );
}

// ---------------------------------------------------------------------------------------------
// Rows: a table, or one card per row on a phone

interface RowActions {
  readonly capOpen: string | undefined;
  readonly manage: boolean;
  readonly onAddCode: (row: UnconfiguredRow) => void;
  readonly onCapOpen: (cityId: string, open: boolean) => void;
  readonly onDrop: (row: ConfiguredRow) => void;
  readonly onEdit: (row: ConfiguredRow) => void;
}

function nameCell(row: CityRow): ReactNode {
  return (
    <span className="flex flex-col gap-1">
      {row.kind === 'configured' ? (
        cityTagLabel(row.usage.cityTag, row.cityNameFa)
      ) : (
        <span className="whitespace-nowrap">{row.cityNameFa}</span>
      )}
      <span className="text-xs font-normal text-muted-foreground">{row.provinceNameFa}</span>
    </span>
  );
}

function meter(row: ConfiguredRow): ReactNode {
  const usage = row.usage;
  return (
    <CapacityMeter
      label={`مصرف سقف ${row.cityNameFa}`}
      rate={usage.usedRate}
      valueText={`${formatCount(usage.liveCount)} از ${formatCount(usage.issuanceCap)}${
        usage.usedRate === null ? '' : `، ${formatRatePercent(usage.usedRate, { invalid: 'raw' })}`
      }`}
    />
  );
}

function stateChip(row: CityRow): ReactNode {
  if (row.kind === 'unconfigured') return <StatusChip title={NONE_LABEL} tone="warning" />;
  const entry = capStateEntry(row.usage.capState);
  return <StatusChip title={entry.label} tone={entry.tone} />;
}

function waiting(row: CityRow): string {
  const count = row.kind === 'configured' ? row.usage.waitingBuyerCount : row.waitingBuyerCount;
  return count === 0 ? '—' : `${formatCount(count)} نفر`;
}

function CapCell({ actions, row }: { readonly actions: RowActions; readonly row: ConfiguredRow }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="tabular-nums">{formatCount(row.usage.issuanceCap)}</span>
      {actions.manage ? (
        <CapEditor
          onOpenChange={(open) => actions.onCapOpen(row.cityId, open)}
          open={actions.capOpen === row.cityId}
          row={row}
        />
      ) : null}
    </span>
  );
}

function RowMenu({ actions, row }: { readonly actions: RowActions; readonly row: CityRow }) {
  if (!actions.manage) return null;
  if (row.kind === 'unconfigured') {
    return (
      <Button
        aria-label={`تعریف کد ${row.cityNameFa}`}
        onClick={() => actions.onAddCode(row)}
        size="sm"
        variant="secondary"
      >
        تعریف کد
      </Button>
    );
  }
  return (
    <Menu
      label={`کارهای ${row.cityNameFa}`}
      trigger={<IconButton icon="more" label={`کارهای ${row.cityNameFa}`} />}
    >
      <MenuItem onSelect={() => actions.onEdit(row)}>ویرایش</MenuItem>
      <MenuItem
        destructive
        disabled={row.usage.locked}
        disabledReason="این شهر کارت دارد؛ حذف نمی‌شود."
        onSelect={() => actions.onDrop(row)}
      >
        حذف
      </MenuItem>
    </Menu>
  );
}

function CityTable({
  actions,
  rows,
}: {
  readonly actions: RowActions;
  readonly rows: readonly CityRow[];
}) {
  return (
    // A focusable region lets keyboard users scroll the table sideways.
    // oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex
    <div
      aria-label="جدول شهرها"
      className="w-full overflow-x-auto rounded-2xl bg-background [&_td:first-child]:ps-4 [&_td:last-child]:pe-4 [&_th:first-child]:ps-4 [&_th:last-child]:pe-4"
      role="region"
      tabIndex={0}
    >
      <ShadTable>
        <caption className="sr-only">مصرف و سقف کارت در هر شهر</caption>
        <TableHeader>
          <TableRow>
            <TableHead scope="col">کد و شهر</TableHead>
            <TableHead scope="col">کارت فعال از سقف</TableHead>
            <TableHead scope="col">وضعیت</TableHead>
            <TableHead className="text-end" scope="col">
              سقف
            </TableHead>
            <TableHead className="text-end" scope="col">
              منتظر کارت
            </TableHead>
            {actions.manage ? (
              <TableHead scope="col">
                <span className="sr-only">کارها</span>
              </TableHead>
            ) : null}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.cityId}>
              <TableCell>{nameCell(row)}</TableCell>
              <TableCell>
                {row.kind === 'configured' ? <div className="min-w-36">{meter(row)}</div> : '—'}
              </TableCell>
              <TableCell>{stateChip(row)}</TableCell>
              <TableCell className="text-end tabular-nums">
                {row.kind === 'configured' ? <CapCell actions={actions} row={row} /> : '—'}
              </TableCell>
              <TableCell className="text-end tabular-nums">{waiting(row)}</TableCell>
              {actions.manage ? (
                <TableCell className="text-end">
                  <RowMenu actions={actions} row={row} />
                </TableCell>
              ) : null}
            </TableRow>
          ))}
        </TableBody>
      </ShadTable>
    </div>
  );
}

function CityCards({
  actions,
  rows,
}: {
  readonly actions: RowActions;
  readonly rows: readonly CityRow[];
}) {
  return (
    <ul aria-label="فهرست شهرها" className="m-0 flex list-none flex-col gap-2 p-0">
      {rows.map((row) => (
        <li className="flex flex-col gap-3 rounded-2xl bg-background p-4" key={row.cityId}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 font-bold">{nameCell(row)}</div>
            <div className="flex shrink-0 items-center gap-2">
              {stateChip(row)}
              <RowMenu actions={actions} row={row} />
            </div>
          </div>
          {row.kind === 'configured' ? meter(row) : null}
          <div className="flex items-center justify-between gap-2 text-sm">
            {row.kind === 'configured' ? (
              <span className="inline-flex items-center gap-1">
                <span className="text-muted-foreground">سقف</span>
                <CapCell actions={actions} row={row} />
              </span>
            ) : (
              <span className="text-muted-foreground">هنوز کد ندارد</span>
            )}
            <span className="tabular-nums">
              <span className="text-muted-foreground">منتظر کارت: </span>
              {waiting(row)}
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------------------------
// Inline cap editor

function CapEditor({
  onOpenChange,
  open,
  row,
}: {
  readonly onOpenChange: (open: boolean) => void;
  readonly open: boolean;
  readonly row: ConfiguredRow;
}) {
  const input = useRef<HTMLInputElement | null>(null);
  return (
    <Popover onOpenChange={onOpenChange} open={open}>
      <PopoverTrigger
        render={
          <ShadButton
            aria-label={`ویرایش سقف ${row.cityNameFa}`}
            className="rounded-full"
            size="icon-sm"
            type="button"
            variant="ghost"
          />
        }
      >
        <PencilIcon aria-hidden="true" className="size-3.5" />
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="glass w-80 rounded-2xl p-3 shadow-sm max-sm:mx-[11px] max-sm:w-[calc(100vw-2rem)]"
        initialFocus={input}
      >
        {open ? <CapForm input={input} onDone={() => onOpenChange(false)} row={row} /> : null}
      </PopoverContent>
    </Popover>
  );
}

function CapForm({
  input,
  onDone,
  row,
}: {
  readonly input: RefObject<HTMLInputElement | null>;
  readonly onDone: () => void;
  readonly row: ConfiguredRow;
}) {
  const save = useNumberingSave('city-cap', onDone);
  const before = row.usage.issuanceCap;
  const [raw, setRaw] = useState(String(before));
  const [reason, setReason] = useState(defaultReason('capUp'));
  const [errors, setErrors] = useState<{
    readonly cap?: string | undefined;
    readonly reason?: string | undefined;
  }>({});
  const cap = Number(raw);
  const valid = capProblem(raw) === undefined;
  const down = valid && cap < before;
  const warning = valid ? capBelowLiveWarning(cap, row.usage.liveCount) : undefined;

  function changeCap(next: string) {
    setRaw(next);
    setErrors((previous) => ({ ...previous, cap: undefined }));
    const nextDown = capProblem(next) === undefined && Number(next) < before;
    // The chosen chip follows the direction until the reason is written by hand.
    if (reason === defaultReason('capUp') || reason === defaultReason('capDown')) {
      setReason(defaultReason(nextDown ? 'capDown' : 'capUp'));
    }
  }

  async function submit() {
    const capError = capProblem(raw) ?? (cap === before ? 'سقف تغییری نکرده است.' : undefined);
    const reasonError = reasonProblem(reason);
    setErrors({
      ...(capError === undefined ? {} : { cap: capError }),
      ...(reasonError === undefined ? {} : { reason: reasonError }),
    });
    if (capError !== undefined || reasonError !== undefined) return;
    await save.save({
      change: setCityCap(row.cityId, cap),
      fields: (values) => ({
        [`cities.${values.cities.findIndex((item) => item.cityId === row.cityId)}.issuanceCap`]:
          'cap',
      }),
      reason,
      setError: (field, message) => setErrors((previous) => ({ ...previous, [field]: message })),
    });
  }

  return (
    <form
      aria-label={`سقف ${row.cityNameFa}`}
      className="flex flex-col gap-3"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <div className="flex items-end gap-1.5">
        <div className="min-w-0 flex-1">
          <TextField
            error={errors.cap}
            hint={`الان ${formatCount(row.usage.liveCount)} کارت فعال`}
            inputMode="numeric"
            label={`سقف ${row.cityNameFa}`}
            ltr
            onChange={(event) => changeCap(digitsOnly(event.target.value))}
            ref={input}
            value={raw}
          />
        </div>
        <div className="flex gap-1 pb-6">
          <IconButton
            disabled={save.blocked || !save.ready}
            icon="check"
            label="ذخیره"
            loading={save.pending}
            type="submit"
            variant="filled"
          />
          <IconButton icon="close" label="انصراف" onClick={onDone} variant="outlined" />
        </div>
      </div>
      {warning === undefined ? null : (
        <p className="text-xs text-muted-foreground" role="status">
          {warning}
        </p>
      )}
      <ReasonPicker
        error={errors.reason}
        onChange={(value) => {
          setReason(value);
          setErrors((previous) => ({ ...previous, reason: undefined }));
        }}
        presets={down ? REASON_PRESETS.capDown : REASON_PRESETS.capUp}
        value={reason}
      />
      <ProblemLine message={save.problem} />
      <FieldErrorList errors={save.unmapped} />
      <CommandFeedback onCheckStatus={save.checkStatus} state={save.feedback} />
    </form>
  );
}

// ---------------------------------------------------------------------------------------------
// Add or edit a city

const cityFormSchema = z
  .object({
    cityCode: z.string(),
    cityCodeWidth: z.enum(['1', '2']),
    cityId: z.string().min(1, 'شهر را انتخاب کنید.'),
    countryDigit: z.string(),
    issuanceCap: z.string(),
    provinceDigit: z.string(),
    provinceId: z.string().min(1, 'استان را انتخاب کنید.'),
    reason: z.string(),
  })
  .superRefine((values, context) => {
    const cap = capProblem(values.issuanceCap);
    if (cap !== undefined)
      context.addIssue({ code: 'custom', message: cap, path: ['issuanceCap'] });
    const reason = reasonProblem(values.reason);
    if (reason !== undefined) {
      context.addIssue({ code: 'custom', message: reason, path: ['reason'] });
    }
  });

type CityForm = z.infer<typeof cityFormSchema>;

const WIDTH_HINT: Readonly<Record<'1' | '2', string>> = {
  1: 'کد هر شهر یک رقم است؛ تا ۱۰ شهر در این استان جا می‌شود.',
  2: 'کد هر شهر دو رقم است؛ تا ۱۰۰ شهر در این استان جا می‌شود.',
};

const PROVINCES_FAILED = 'فهرست استان‌ها باز نشد؛ پنجره را ببندید و دوباره باز کنید.';

function CityDialog({
  dialog,
  onClose,
  provinceOf,
  provinces,
  provincesFailed,
}: {
  readonly dialog: Exclude<Dialog, { kind: 'drop' }>;
  readonly onClose: () => void;
  readonly provinceOf: (cityId: string) => string | undefined;
  readonly provinces: readonly GeographyArea[];
  readonly provincesFailed: boolean;
}) {
  const edit = dialog.kind === 'edit' ? dialog.row : undefined;
  const save = useNumberingSave(edit === undefined ? 'city-add' : 'city-edit', onClose);
  const values = save.version === undefined ? undefined : valuesOf(save.version);
  const form = useForm<CityForm>({
    defaultValues: {
      cityCode: edit === undefined ? '' : edit.usage.cityTag.slice(2),
      cityCodeWidth: '1',
      cityId: edit?.cityId ?? (dialog.kind === 'add' ? (dialog.cityId ?? '') : ''),
      countryDigit: '',
      issuanceCap: edit === undefined ? '' : String(edit.usage.issuanceCap),
      provinceDigit: '',
      provinceId: edit?.provinceId ?? (dialog.kind === 'add' ? (dialog.provinceId ?? '') : ''),
      reason: defaultReason(edit === undefined ? 'addCity' : 'editCity'),
    },
    resolver: zodResolver(cityFormSchema),
  });
  const typed = useRef({ code: false, digits: false });
  const provinceId = form.watch('provinceId');
  const cityId = form.watch('cityId');
  const width = form.watch('cityCodeWidth');
  const configured = values?.provinces.find((row) => row.provinceId === provinceId);
  const geographyCities = useCities(provinceId === '' ? undefined : provinceId);
  const codeWidth: CodeWidth = configured?.cityCodeWidth ?? (width === '2' ? 2 : 1);
  const used = useMemo(
    () =>
      values === undefined || configured === undefined
        ? new Set<string>()
        : usedCityCodes(values, provinceId, provinceOf, edit?.cityId),
    [values, configured, provinceId, provinceOf, edit?.cityId],
  );
  const suggestedCode = provinceId === '' ? '' : (nextCityCode(codeWidth, used) ?? '');
  const suggestedDigits = values === undefined ? undefined : nextProvinceDigits(values.provinces);
  const cityCount = geographyCities.data?.length;

  // Suggestions follow the province and the width until the field is typed in.
  useEffect(() => {
    if (edit === undefined && !typed.current.code) form.setValue('cityCode', suggestedCode);
  }, [edit, form, suggestedCode]);
  useEffect(() => {
    if (edit !== undefined) return;
    if (configured !== undefined || typed.current.digits || suggestedDigits === undefined) return;
    form.setValue('countryDigit', String(suggestedDigits.countryDigit));
    form.setValue('provinceDigit', String(suggestedDigits.provinceDigit));
  }, [configured, edit, form, suggestedDigits?.countryDigit, suggestedDigits?.provinceDigit]);
  useEffect(() => {
    if (edit === undefined && configured === undefined && cityCount !== undefined) {
      form.setValue('cityCodeWidth', cityCount > 9 ? '2' : '1');
    }
  }, [cityCount, configured, edit, form]);

  const taken = new Set(values?.cities.map((row) => row.cityId) ?? []);
  const cityOptions = (geographyCities.data ?? [])
    .filter((city) => city.id === cityId || !taken.has(city.id))
    .map((city) => ({ label: city.nameFa, value: city.id }));
  const cityName =
    edit?.cityNameFa ?? geographyCities.data?.find((city) => city.id === cityId)?.nameFa;
  const code = form.watch('cityCode');
  const digits = configured ?? {
    countryDigit: Number(form.watch('countryDigit')),
    provinceDigit: Number(form.watch('provinceDigit')),
  };
  const tag =
    code.length === codeWidth && /^[0-9]+$/.test(code) && cityName !== undefined
      ? cityTagOf(digits, code)
      : undefined;
  const errors = form.formState.errors;
  const locked = edit?.usage.locked === true;
  const typedCap = form.watch('issuanceCap');
  const capWarning =
    edit !== undefined && capProblem(typedCap) === undefined
      ? capBelowLiveWarning(Number(typedCap), edit.usage.liveCount)
      : undefined;

  async function submit(data: CityForm) {
    if (values === undefined) return;
    const problems: [keyof CityForm, string][] = [];
    let province = configured;
    if (province === undefined) {
      const country = data.countryDigit;
      const own = data.provinceDigit;
      if (!/^[0-9]$/.test(country)) problems.push(['countryDigit', 'یک رقم از ۰ تا ۹.']);
      if (!/^[0-9]$/.test(own)) problems.push(['provinceDigit', 'یک رقم از ۰ تا ۹.']);
      else if (
        values.provinces.some(
          (row) => row.countryDigit === Number(country) && row.provinceDigit === Number(own),
        )
      ) {
        problems.push(['provinceDigit', 'این کد استان را استان دیگری دارد.']);
      }
      province = {
        cityCodeWidth: data.cityCodeWidth === '2' ? 2 : 1,
        countryDigit: Number(country),
        provinceDigit: Number(own),
        provinceId: data.provinceId,
      };
    }
    const codeError = locked ? undefined : cityCodeProblem(data.cityCode, codeWidth, used);
    if (codeError !== undefined) problems.push(['cityCode', codeError]);
    if (problems.length > 0) {
      problems.forEach(([field, message], index) =>
        form.setError(field, { message, type: 'validate' }, { shouldFocus: index === 0 }),
      );
      return;
    }
    const newProvince = configured === undefined ? province : undefined;
    const city = {
      cityCode: data.cityCode,
      cityId: data.cityId,
      issuanceCap: Number(data.issuanceCap),
    };
    const change: NumberingChange =
      newProvince === undefined ? putCity(city) : both(putProvince(newProvince), putCity(city));
    await save.save({
      change,
      fields: (next) => {
        const at = next.cities.findIndex((row) => row.cityId === city.cityId);
        const pAt = next.provinces.findIndex((row) => row.provinceId === data.provinceId);
        return {
          [`cities.${at}.cityCode`]: 'cityCode',
          [`cities.${at}.cityId`]: 'cityId',
          [`cities.${at}.issuanceCap`]: 'issuanceCap',
          [`provinces.${pAt}.countryDigit`]: 'countryDigit',
          [`provinces.${pAt}.provinceDigit`]: 'provinceDigit',
          [`provinces.${pAt}.cityCodeWidth`]: 'cityCodeWidth',
        };
      },
      reason: data.reason,
      setError: (field, message) =>
        form.setError(field as keyof CityForm, { message, type: 'server' }),
    });
  }

  const title = edit === undefined ? 'افزودن شهر' : `ویرایش ${edit.cityNameFa}`;
  return (
    <ChangeDialog
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      onSubmit={() => void form.handleSubmit(submit)()}
      open
      reason={
        <ReasonPicker
          error={errors.reason?.message}
          onChange={(value) => form.setValue('reason', value, { shouldValidate: true })}
          presets={REASON_PRESETS[edit === undefined ? 'addCity' : 'editCity']}
          value={form.watch('reason')}
        />
      }
      save={save}
      title={title}
    >
      {edit === undefined ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <NativeSelect
            error={errors.provinceId?.message ?? (provincesFailed ? PROVINCES_FAILED : undefined)}
            label="استان"
            options={[...provinces]
              .sort((left, right) => left.nameFa.localeCompare(right.nameFa, 'fa'))
              .map((item) => ({ label: item.nameFa, value: item.id }))}
            placeholder="انتخاب استان"
            {...form.register('provinceId', {
              onChange: () => {
                form.setValue('cityId', '');
                typed.current = { code: false, digits: false };
              },
            })}
            value={provinceId}
          />
          <NativeSelect
            disabled={provinceId === ''}
            error={errors.cityId?.message}
            label="شهر"
            options={cityOptions}
            placeholder={geographyCities.isLoading ? 'در حال بارگذاری…' : 'انتخاب شهر'}
            {...form.register('cityId')}
            // Controlled: the options arrive after the pre-filled value.
            value={cityId}
          />
        </div>
      ) : (
        <p className="text-sm">
          <strong>{edit.cityNameFa}</strong>
          <span className="text-muted-foreground">{`، ${edit.provinceNameFa}`}</span>
        </p>
      )}
      {edit === undefined &&
      provinceId !== '' &&
      values !== undefined &&
      configured === undefined ? (
        <fieldset className="flex flex-col gap-3 rounded-2xl bg-muted p-3">
          <legend className="sr-only">کد استان</legend>
          <p className="text-sm font-medium">این استان هنوز کد ندارد؛ همین‌جا تعریف می‌شود.</p>
          <div className="grid grid-cols-2 gap-3">
            <TextField
              error={errors.countryDigit?.message}
              inputMode="numeric"
              label="رقم کشور"
              ltr
              maxLength={1}
              {...form.register('countryDigit', {
                onChange: () => (typed.current.digits = true),
                setValueAs: digitsOnly,
              })}
            />
            <TextField
              error={errors.provinceDigit?.message}
              inputMode="numeric"
              label="رقم استان"
              ltr
              maxLength={1}
              {...form.register('provinceDigit', {
                onChange: () => (typed.current.digits = true),
                setValueAs: digitsOnly,
              })}
            />
          </div>
          <SegmentedButtons
            label="نوع کد شهرهای این استان"
            onChange={(key) => form.setValue('cityCodeWidth', key === '2' ? '2' : '1')}
            options={[
              { key: '1', label: WIDTH_LABEL[1] },
              { key: '2', label: WIDTH_LABEL[2] },
            ]}
            value={width}
          />
          <p className="text-xs text-muted-foreground">{WIDTH_HINT[width]}</p>
        </fieldset>
      ) : null}
      <div className="grid gap-3 sm:grid-cols-2">
        <TextField
          error={errors.cityCode?.message}
          hint={
            locked
              ? 'در این شهر کارت صادر شده؛ کد ثابت می‌ماند.'
              : codeWidth === 1
                ? 'یک رقم'
                : 'دو رقم، مثل ۰۱'
          }
          inputMode="numeric"
          label="کد شهر"
          ltr
          maxLength={codeWidth}
          readOnly={locked}
          {...form.register('cityCode', {
            onChange: () => (typed.current.code = true),
            setValueAs: digitsOnly,
          })}
        />
        <TextField
          error={errors.issuanceCap?.message}
          hint={
            edit === undefined
              ? 'حداکثر کارت فعال این شهر'
              : `الان ${formatCount(edit.usage.liveCount)} کارت فعال`
          }
          inputMode="numeric"
          label="سقف صدور"
          ltr
          {...form.register('issuanceCap', { setValueAs: digitsOnly })}
        />
      </div>
      {capWarning === undefined ? null : (
        <p className="text-xs text-muted-foreground" role="status">
          {capWarning}
        </p>
      )}
      <p aria-live="polite" className="text-sm">
        {tag === undefined ? (
          <span className="text-muted-foreground">کد کامل بعد از انتخاب شهر و کد دیده می‌شود.</span>
        ) : (
          <>
            <span className="text-muted-foreground">کد کامل: </span>
            {cityTagLabel(tag, cityName ?? '')}
          </>
        )}
      </p>
    </ChangeDialog>
  );
}

function DropCityDialog({
  onClose,
  row,
}: {
  readonly onClose: () => void;
  readonly row: ConfiguredRow;
}) {
  const save = useNumberingSave('city-drop', onClose);
  const [reason, setReason] = useState(defaultReason('dropCity'));
  const [error, setError] = useState<string | undefined>();
  return (
    <ChangeDialog
      description={`کد ${toPersianDigits(row.usage.cityTag)} آزاد می‌شود و خریدارهای ${row.cityNameFa} تا تعریف دوباره منتظر کارت می‌مانند.`}
      destructive
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      onSubmit={() => {
        const problem = reasonProblem(reason);
        setError(problem);
        if (problem === undefined) void save.save({ change: dropCity(row.cityId), reason });
      }}
      open
      reason={
        <ReasonPicker
          error={error}
          onChange={setReason}
          presets={REASON_PRESETS.dropCity}
          value={reason}
        />
      }
      save={save}
      submitLabel="حذف شهر"
      title={`حذف ${row.cityNameFa}`}
    />
  );
}
